import { styled } from '@mui/material';
import classNames from 'classnames';

import { AircraftData } from '#entities';

import { EZLayers, ZLayer } from '../../data-context';
import { getAircraftStatus, getAircraftTimeSeverity } from '../../helpers';
import { useSelectHighlightedAircraftRegistration } from '../../widgetManager';
import { useOverlay } from '../model';

import type { SVGProps } from 'react';

export interface AviationAirportAircraftsProps extends SVGProps<SVGGElement> {
  aircraftTailNumbers: string[];
  aircraftsByTailNumbers: Record<string, AircraftData>;
  x: number;
  y: number;
}

const PREFIX = 'TSMED-AviationAirportAircrafts';
const aviationAirportAircraftsClasses = {
  airportWrapper: `${PREFIX}-airportWrapper`,
  airportHighlight: `${PREFIX}-airportHighlight`,
  airportMark: `${PREFIX}-airportMark`,
  airportMarkFlightCategory: `${PREFIX}-airportMarkFlightCategory`,

  airportLabel: `${PREFIX}-airportLabel`,
  airportAircraftsWrapper: `${PREFIX}-airportAircraftsWrapper`,
  airportAircraftsMark: `${PREFIX}-airportAircraftsMark`,
  aircraftWrapper: `${PREFIX}-aircraftWrapper`,
  aircraft: `${PREFIX}-aircraft`,
};

const Root = styled('text')(({ theme: { palette, mixins, utils } }) => ({
  ...mixins.aircraftStatusStyles(palette, { prefix: '&' }),
  ...mixins.severityStyles(palette, { prefix: '&' }),
  ...mixins.organStyles(palette, { prefix: '&' }),
  textShadow: '0px 0px 3px rgba(0, 0, 0, 1)',
  color: `var(--primaryColor, ${palette.grey[200]})`,
  fill: 'currentColor',
  cursor: 'pointer',
  transition: 'all 250ms ease-in-out',
  fontSize: utils.clamp(10, 14, 'sm-xl'),
  fontFamily: '"JetBrains Mono", monospace',
  fontWeight: 500,
  ['&.suppressed']: {
    opacity: 0.3,
  },

  ['&:hover']: {
    textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
    strokeWidth: 4,
    stroke: 'black',
    paintOrder: 'stroke',
  },
}));

export const AviationAirportAircrafts = ({
  aircraftTailNumbers,
  aircraftsByTailNumbers: aircraftsByTailNumbers,
  x,
  y,
  ...rest
}: AviationAirportAircraftsProps) => {
  const {
    onAircraftSelect,
    handleHighlightAircraft,

    isInRotation,
  } = useOverlay();

  const highlightedAircraft = useSelectHighlightedAircraftRegistration();

  return (
    <g {...rest}>
      {aircraftTailNumbers.map((aircraftId, index) => {
        const aircraft = aircraftsByTailNumbers[aircraftId];
        const actualTimeSeverity = getAircraftTimeSeverity(aircraft);
        const actualStatus = getAircraftStatus(aircraft);
        const zIndex =
          highlightedAircraft === aircraft.registration
            ? EZLayers.Highlighted1
            : EZLayers.Foreground;
        const isSuppressed =
          highlightedAircraft &&
          highlightedAircraft !== aircraft.registration &&
          !isInRotation.current;
        return (
          <ZLayer key={aircraftId} zIndex={zIndex}>
            <Root
              onClick={() => {
                onAircraftSelect(aircraft.Id);
              }}
              onTouchEnd={() => {
                onAircraftSelect(aircraft.Id);
              }}
              onMouseEnter={() => {
                handleHighlightAircraft(aircraft.registration);
              }}
              onMouseLeave={() => handleHighlightAircraft()}
              className={classNames(
                aviationAirportAircraftsClasses.airportAircraftsMark,

                `aircraft-status-${actualStatus}`,
                {
                  suppressed: isSuppressed,
                  [`severity-status-${actualTimeSeverity}`]:
                    !aircraft.maintenanceType,
                  [`organ-type-${aircraft.actualOrgan}`]: aircraft.actualOrgan,
                  [`maintenance-type-${aircraft.maintenanceType?.OpsBoardBadge}`]:
                    aircraft.maintenanceType?.OpsBoardBadge,
                },
              )}
              x={x}
              y={y + index * 20}
            >
              {aircraft.Registration}
            </Root>
          </ZLayer>
        );
      })}
    </g>
  );
};
